import React, { useState, useEffect } from 'react';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import axios from 'axios';
import './RichEditorModal.css';
import { useTranslation } from '../redux/TranslationHelper';

const RichEditorModal = ({ isOpen, onClose, initialContent, onSave }) => {
  const { t } = useTranslation();
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [htmlContent, setHtmlContent] = useState('');
  const [editorMode, setEditorMode] = useState('wysiwyg'); // wysiwyg, html, preview
  
  // 처음 모달이 열릴 때와 initialContent가 변경될 때 에디터 초기화
  // useEffect(() => {
  //   if (isOpen) {
  //     const html = initialContent || '';
  //     setHtmlContent(html);
      
  //     // HTML을 Draft.js 형식으로 변환
  //     if (html) {
  //       const contentBlock = htmlToDraft(html);
  //       if (contentBlock) {
  //         const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
  //         const editorState = EditorState.createWithContent(contentState);
  //         setEditorState(editorState);
  //       }
  //     } else {
  //       setEditorState(EditorState.createEmpty());
  //     }
  //   }
  // }, [isOpen, initialContent]);
  

  useEffect(() => {
    if (isOpen) {
      // initialContent가 문자열인지 확인하고, 아니면 빈 문자열로 설정
      const html = typeof initialContent === 'string' ? initialContent : '';
      setHtmlContent(html);
      
      // HTML을 Draft.js 형식으로 변환
      if (html) {
        const contentBlock = htmlToDraft(html);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          const editorState = EditorState.createWithContent(contentState);
          setEditorState(editorState);
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    }
  }, [isOpen, initialContent]);

  // editorState가 변경될 때마다 HTML 변환
  const onEditorStateChange = (editorState) => {
    setEditorState(editorState);
    const rawContentState = convertToRaw(editorState.getCurrentContent());
    const html = draftToHtml(rawContentState);
    setHtmlContent(html);
  };
  
  // HTML 직접 편집 시 사용할 핸들러
  const handleHtmlChange = (e) => {
    const newHtml = e.target.value;
    setHtmlContent(newHtml);
    
    // HTML이 변경될 때마다 editorState도 업데이트 (필요시)
    if (editorMode === 'wysiwyg') {
      try {
        const contentBlock = htmlToDraft(newHtml);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
        }
      } catch (error) {
        console.error(t('HTML 변환 오류:'), error);
      }
    }
  };
  
  const handleSave = () => {
    onSave(htmlContent);
    onClose();
  };
  
  // refrigerator API를 이용한 이미지 업로드 함수
  const uploadImageCallBack = (file) => {
    return new Promise(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucket', 'withcookie-bucket');
        formData.append('path', 'c/default');

        const res = await axios.post(
          `https://refrigerator.logipasta.com/v1/file`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Control-Allow-Origin': '*',
            },
          }
        );

        if (res.data && res.data.file) {
          resolve({
            data: {
              link: res.data.file
            }
          });
        } else {
          reject(t('이미지 URL을 가져올 수 없습니다.'));
        }
      } catch (err) {
        console.error(t('이미지 업로드 실패:'), err);
        reject(t('이미지 업로드에 실패했습니다.'));
      }
    });
  };
  
  // HTML 편집기에서 이미지 업로드 버튼 핸들러
  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('bucket', 'withcookie-bucket');
        formData.append('path', 'c/default');

        const res = await axios.post(
          `https://refrigerator.logipasta.com/v1/file`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Access-Control-Allow-Origin': '*',
            },
          }
        );

        if (res.data && res.data.file) {
          const imageUrl = res.data.file;
          const imageTag = `<img src="${imageUrl}" alt="${t('이미지')}" style="max-width: 100%; height: auto;" />`;
          
          const textarea = document.getElementById('html-editor-textarea');
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const newContent = htmlContent.substring(0, start) + imageTag + htmlContent.substring(end);
            setHtmlContent(newContent);
            
            setTimeout(() => {
              textarea.focus();
              textarea.selectionStart = start + imageTag.length;
              textarea.selectionEnd = start + imageTag.length;
            }, 0);
          } else {
            setHtmlContent(prev => prev + imageTag);
          }
        }
      } catch (err) {
        console.error(t('이미지 업로드 실패:'), err);
        alert(t('이미지 업로드에 실패했습니다. 다시 시도해주세요.'));
      }
    };
  };
  
  const addFormatting = (tag, attributes = '') => {
    const textarea = document.getElementById('html-editor-textarea');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = htmlContent.substring(start, end);
    
    let formattedText;
    if (tag === 'hr') {
      // 특수 태그(닫는 태그 없는 경우)
      formattedText = `<${tag}${attributes ? ' ' + attributes : ''}>`;
    } else {
      formattedText = `<${tag}${attributes ? ' ' + attributes : ''}>${selectedText}</${tag}>`;
    }
    
    const newContent = htmlContent.substring(0, start) + formattedText + htmlContent.substring(end);
    setHtmlContent(newContent);
    
    // 커서 위치 조정
    setTimeout(() => {
      textarea.focus();
      if (selectedText) {
        textarea.selectionStart = start;
        textarea.selectionEnd = start + formattedText.length;
      } else {
        const cursorPos = start + tag.length + 2 + (attributes ? attributes.length + 1 : 0);
        textarea.selectionStart = cursorPos;
        textarea.selectionEnd = cursorPos;
      }
    }, 0);
  };
  
  const insertSampleContent = () => {
    const sampleHtml = `
<h2>${t('제목을 입력하세요')}</h2>
<p>${t('이곳에 내용을 입력하세요.')} <strong>${t('굵게')}</strong> ${t('또는')} <em>${t('기울임꼴')}</em>${t('로 강조할 수 있습니다.')}</p>
<ul>
  <li>${t('항목')} 1</li>
  <li>${t('항목')} 2</li>
  <li>${t('항목')} 3</li>
</ul>
<p><a href="https://example.com">${t('링크 텍스트')}</a></p>
<blockquote>${t('인용구를 입력하세요.')}</blockquote>
    `.trim();
    setHtmlContent(sampleHtml);
    
    // HTML을 Draft.js 형식으로 변환 (필요시)
    if (editorMode === 'wysiwyg') {
      try {
        const contentBlock = htmlToDraft(sampleHtml);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
          const newEditorState = EditorState.createWithContent(contentState);
          setEditorState(newEditorState);
        }
      } catch (error) {
        console.error(t('HTML 변환 오류:'), error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rich-editor-modal-overlay">
      <div className="rich-editor-modal">
        <div className="rich-editor-modal-header">
          <h2>{t('콘텐츠 편집')}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="editor-mode-toggle">
          <button 
            className={editorMode === 'wysiwyg' ? 'active' : ''} 
            onClick={() => setEditorMode('wysiwyg')}
          >
            {t('자유 형식 편집기')}
          </button>
          <button 
            className={editorMode === 'html' ? 'active' : ''} 
            onClick={() => setEditorMode('html')}
          >
            {t('HTML 편집기')}
          </button>
          <button 
            className={editorMode === 'preview' ? 'active' : ''} 
            onClick={() => setEditorMode('preview')}
          >
            {t('미리보기')}
          </button>
        </div>
        
        {editorMode === 'wysiwyg' && (
          <div className="rich-editor-modal-body">
            <Editor
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              wrapperClassName="draft-editor-wrapper"
              editorClassName="draft-editor"
              toolbar={{
                options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'image', 'remove', 'history'],
                inline: {
                  options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                },
                blockType: {
                  options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
                },
                image: {
                  uploadCallback: uploadImageCallBack,
                  alt: { present: true, mandatory: false },
                  previewImage: true,
                  defaultSize: {
                    height: 'auto',
                    width: '100%',
                  },
                },
              }}
              localization={{
                locale: 'ko',
              }}
              placeholder={t('내용을 입력하세요...')}
            />
          </div>
        )}
        
        {editorMode === 'html' && (
          <div className="rich-editor-modal-body">
            <div className="formatting-toolbar">
              <button onClick={() => addFormatting('h2')} title={t('제목 2')}>H2</button>
              <button onClick={() => addFormatting('h3')} title={t('제목 3')}>H3</button>
              <button onClick={() => addFormatting('p')} title={t('문단')}>P</button>
              <button onClick={() => addFormatting('strong')} title={t('굵게')}>B</button>
              <button onClick={() => addFormatting('em')} title={t('기울임')}>I</button>
              <button onClick={() => addFormatting('u')} title={t('밑줄')}>U</button>
              <button onClick={() => addFormatting('a', 'href="https://example.com"')} title={t('링크')}>A</button>
              <button onClick={() => addFormatting('ul')} title={t('목록')}>UL</button>
              <button onClick={() => addFormatting('li')} title={t('목록 항목')}>LI</button>
              <button onClick={() => addFormatting('blockquote')} title={t('인용구')}>Q</button>
              <button onClick={() => addFormatting('hr')} title={t('구분선')}>HR</button>
              <button onClick={handleImageUpload} title={t('이미지 추가')}>IMG</button>
              <button onClick={insertSampleContent} title={t('샘플 콘텐츠')}>{t('샘플')}</button>
            </div>
            <div className="html-editor">
              <textarea
                id="html-editor-textarea"
                value={htmlContent}
                onChange={handleHtmlChange}
                placeholder={t('HTML 코드를 직접 작성하세요...')}
              />
            </div>
          </div>
        )}
        
        {editorMode === 'preview' && (
          <div className="preview-container">
            <div
              className="preview-content"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        )}
        
        <div className="rich-editor-modal-footer">
          <button className="cancel-button" onClick={onClose}>{t('취소')}</button>
          <button className="save-button" onClick={handleSave}>{t('저장')}</button>
        </div>
      </div>
    </div>
  );
};

export default RichEditorModal; 